import {SearchType} from '../../../../../app/pages/search/types'

/**
 * Presentational metadata for each search type: dropdown label, adaptive input placeholder, and a
 * Metronic duotune icon (rendered via KTSVG). Kept framework-free so the placeholder/label mappings
 * are unit-tested.
 *
 * Icon choices — Metronic 8 duotune, closest available to the brief's requested glyphs. Where an
 * exact match does not exist in this icon set it is an approximation (noted):
 *   all                → general/gen024      (grid)                     ✓ requested "grid"
 *   members            → communication/com006 (single user)            ✓ requested "user"
 *   experiences        → maps/map008          (location/compass)       ~ approximates "compass"
 *   forums             → communication/com012 (chat bubbles)           ✓ requested "chat bubbles"
 *   mastermind_circles → communication/com008 (group of users)         ✓ requested "users-group"
 *                         (com013/com014 "teams" do not exist in this build)
 *   expertise          → finance/fin006       (case / portfolio)       ~ approximates "briefcase"
 */
export interface SearchTypeMeta {
  value: SearchType
  label: string
  placeholder: string
  icon: string
}

const ICON_BASE = '/media/icons/duotune'

// User-selectable scopes shown in the type-selector dropdown. Aligned with the current header
// mockup — Mastermind Circles is intentionally omitted from the selector; the aggregator still
// returns a mastermind_circles bucket in "all" results, so results-page routing keeps its entry
// in searchTypeMeta / routing (see below in metaForType fallback).
export const SEARCH_TYPE_META: SearchTypeMeta[] = [
  {value: 'all', label: 'All', placeholder: 'Search', icon: `${ICON_BASE}/general/gen024.svg`},
  {value: 'members', label: 'Members', placeholder: 'Search', icon: `${ICON_BASE}/communication/com006.svg`},
  {value: 'experiences', label: 'Experiences', placeholder: 'Search', icon: `${ICON_BASE}/maps/map008.svg`},
  {value: 'expertise', label: 'Expert', placeholder: 'Search', icon: `${ICON_BASE}/finance/fin006.svg`},
  {value: 'forums', label: 'Forum', placeholder: 'Search', icon: `${ICON_BASE}/communication/com012.svg`},
]

// Metadata for buckets that the aggregator can still return but that are NOT user-selectable in
// the type-selector dropdown (e.g. Mastermind Circles). Kept so labelForType / iconForType keep
// working when we render the mixed "all" results dropdown.
const HIDDEN_TYPE_META: SearchTypeMeta[] = [
  {value: 'mastermind_circles', label: 'Mastermind Circles', placeholder: 'Search', icon: `${ICON_BASE}/communication/com008.svg`},
]

/** Magnifier icon for the submit button. */
export const SEARCH_SUBMIT_ICON = `${ICON_BASE}/general/gen021.svg`

const META_BY_TYPE: Record<SearchType, SearchTypeMeta> = [
  ...SEARCH_TYPE_META,
  ...HIDDEN_TYPE_META,
].reduce(
  (acc, m) => {
    acc[m.value] = m
    return acc
  },
  {} as Record<SearchType, SearchTypeMeta>
)

/** Metadata for a type, always falling back to the 'all' entry for an unknown value. */
export const metaForType = (type: SearchType): SearchTypeMeta => META_BY_TYPE[type] ?? META_BY_TYPE.all

export const placeholderForType = (type: SearchType): string => metaForType(type).placeholder

export const labelForType = (type: SearchType): string => metaForType(type).label

export const iconForType = (type: SearchType): string => metaForType(type).icon
